/**
 * Norce Checkout Module
 *
 * A self-contained module for Norce Checkout with multiple payment adapters:
 * - Adyen: Card payments, Apple Pay, Google Pay via Adyen Drop-in
 * - Klarna: Klarna Checkout widget (embedded HTML snippet)
 * - Walley: Walley Checkout widget (embedded HTML snippet)
 * - NonPsp: Invoice payments, free orders (no external PSP)
 *
 * Usage:
 * ```typescript
 * import {
 *   useNorceCheckout,
 *   useNorceCheckoutApi,
 *   NorceCheckoutPayment,
 *   NorceCheckoutAdyen,
 *   NorceCheckoutKlarna,
 *   NorceCheckoutWalley,
 *   isNorceCheckoutPaymentMethod,
 *   NORCE_CHECKOUT_SERVICE_NAME
 * } from '~/composables/norce-checkout';
 * ```
 */

// Constants
export const NORCE_CHECKOUT_SERVICE_NAME = 'NorceCheckoutV2';

// Types
export * from './types';

// Composables
export { default as useNorceCheckout, type PaymentStatusResult } from './useNorceCheckout';
export { default as useNorceCheckoutApi } from './useNorceCheckoutApi';

// Payment session utilities
export {
  savePaymentSession,
  getPaymentSession,
  clearPaymentSession,
  buildSuccessUrl,
  parsePaymentContext,
  type PaymentSessionData,
} from './usePaymentSession';

// Components
export { default as NorceCheckoutPayment } from './NorceCheckoutPayment.vue';
export { default as NorceCheckoutAdyen } from './NorceCheckoutAdyen.vue';
export { default as NorceCheckoutKlarna } from './NorceCheckoutKlarna.vue';
export { default as NorceCheckoutKlarnaConfirmation } from './NorceCheckoutKlarnaConfirmation.vue';
export { default as NorceCheckoutWalley } from './NorceCheckoutWalley.vue';
export { default as NorceCheckoutWalleyConfirmation } from './NorceCheckoutWalleyConfirmation.vue';

// Helper types for payment method detection
export type PaymentMethodService = {
  id: number;
  name: string;
  description: string | null;
  imagePath: string | null;
  imageKey: string | null;
};

export type PaymentMethodWithService = {
  id: number;
  name: string;
  selected: boolean;
  service?: PaymentMethodService;
  [key: string]: unknown;
};

/**
 * Check if a payment method uses Norce Checkout
 * Can be used standalone without depending on store getters
 */
export function isNorceCheckoutPaymentMethod(paymentMethod: PaymentMethodWithService | null | undefined): boolean {
  return paymentMethod?.service?.name === NORCE_CHECKOUT_SERVICE_NAME;
}

/**
 * Check if the selected payment method in an array uses Norce Checkout
 * Useful for checking payment methods from cart/checkout state
 */
export function hasNorceCheckoutSelected(paymentMethods: PaymentMethodWithService[]): boolean {
  const selectedMethod = paymentMethods.find(method => method.selected);
  return isNorceCheckoutPaymentMethod(selectedMethod);
}

// ============================================
// Address Mapping Helpers (for B2C checkout)
// ============================================

import type { NorceCheckoutAddress, UpdateAddressRequest } from './types';
import type { CheckoutAddressDetails } from '#constants/types/norce';

/**
 * Map Norce Checkout address response to form data format
 * Used to pre-fill B2C checkout forms from Norce Checkout customer data
 */
export function mapNorceCheckoutAddressToForm(
  address: NorceCheckoutAddress | undefined,
  fallbackCountry = ''
): CheckoutAddressDetails {
  if (!address) {
    return {
      firstName: '',
      lastName: '',
      name: '',
      email: '',
      phone: '',
      street: '',
      zip: '',
      city: '',
      country: fallbackCountry,
      state: '',
      companyName: null,
    };
  }

  return {
    firstName: address.givenName ?? '',
    lastName: address.familyName ?? '',
    name: address.givenName && address.familyName
      ? `${address.givenName} ${address.familyName}`.trim()
      : '',
    email: address.email ?? '',
    phone: address.phone ?? '',
    // mapFormToNorceCheckoutRequest sends the name as line1 (streetAddress) and the street as line2 (streetAddress2)
    street: address.streetAddress2 || address.streetAddress || '',
    zip: address.postalCode ?? '',
    city: address.city ?? '',
    country: address.country ?? fallbackCountry,
    state: address.region ?? '',
    companyName: address.organizationName ?? null,
  };
}

/**
 * Map form data to Norce Checkout update request format
 * Used when updating addresses via Norce Checkout API for B2C
 */
export function mapFormToNorceCheckoutRequest(
  orderId: string,
  form: CheckoutAddressDetails
): UpdateAddressRequest {
  return {
    orderId,
    email: form.email ?? undefined,
    phone: form.phone ?? undefined,
    firstName: form.firstName ?? form.name ?? undefined,
    lastName: form.lastName ?? undefined,
    companyName: form.companyName ?? undefined,
    deliveryAddress: {
      line1: (form.firstName && form.lastName)
        ? `${form.firstName} ${form.lastName}`.trim()
        : form.name ?? undefined,
      line2: form.street ?? undefined,
      zip: form.zip ?? undefined,
      city: form.city ?? undefined,
      country: form.country ?? undefined,
      region: form.state ?? undefined,
    },
  };
}
